import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, CheckCircle, XCircle, Calendar, Trophy, ArrowLeft, User } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";

export default function FightOffers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'accept' or 'decline'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      const { data } = await api.get("/arena-circuit/fight-offers");
      // Filter only pending offers
      const pendingOffers = (data.offers || []).filter(o => o.status === "pending");
      setOffers(pendingOffers);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedOffer) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/arena-circuit/fight-offers/${selectedOffer.id}/respond`, {
        decision: "accept"
      });
      toast.success(data.message || "Fight offer accepted!", { 
        icon: <Swords size={16} /> 
      });
      setActionModal(null);
      setSelectedOffer(null);
      await loadOffers();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedOffer) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/arena-circuit/fight-offers/${selectedOffer.id}/respond`, {
        decision: "decline"
      });
      toast.success(data.message || "Fight offer declined");
      setActionModal(null);
      setSelectedOffer(null);
      await loadOffers();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  const pendingCount = offers.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          icon={Swords}
          title="Fight Offers"
          subtitle={`${pendingCount} pending offer${pendingCount !== 1 ? "s" : ""}`}
        />
      </div>

      {offers.length === 0 ? (
        <Empty label="No Fight Offers">
          <p className="text-sm text-zinc-400">
            You don&apos;t have any pending fight offers for your gladiators
          </p>
        </Empty>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className="border border-amber-900/30 bg-zinc-950 p-5 hover:border-amber-600/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Trophy size={20} className="text-amber-400" />
                    <div>
                      <h3 className="font-cinzel text-lg text-amber-300">
                        {offer.fight_card_name}
                      </h3>
                      <p className="text-xs text-zinc-500">
                        {offer.circuit_name}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Your Fighter
                      </div>
                      <div className="text-amber-300 font-cinzel flex items-center gap-2">
                        <User size={14} />
                        {offer.fighter_name}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Opponent
                      </div>
                      <div className="text-zinc-200 flex items-center gap-2">
                        <Swords size={14} className="text-red-400" />
                        {offer.opponent_name}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Event Date
                      </div>
                      <div className="text-zinc-200 flex items-center gap-1">
                        <Calendar size={14} className="text-amber-400" />
                        {new Date(offer.event_date).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Event Time
                      </div>
                      <div className="text-zinc-200 flex items-center gap-1">
                        <Calendar size={14} className="text-amber-400" />
                        {new Date(offer.event_date).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-zinc-500">
                    Offered {new Date(offer.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex flex-col gap-2 min-w-[120px]">
                  <button
                    onClick={() => {
                      setSelectedOffer(offer);
                      setActionModal("accept");
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white transition-colors flex items-center justify-center gap-2 text-sm font-cinzel"
                  >
                    <CheckCircle size={14} />
                    Accept
                  </button>
                  <button
                    onClick={() => {
                      setSelectedOffer(offer);
                      setActionModal("decline");
                    }}
                    className="px-4 py-2 bg-red-900/20 border border-red-700/40 text-red-300 hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2 text-sm font-cinzel"
                  >
                    <XCircle size={14} />
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Accept Modal */}
      {actionModal === "accept" && selectedOffer && (
        <Modal open onClose={() => setActionModal(null)} title="Accept Fight Offer">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Accept fight offer for <span className="text-amber-300 font-cinzel font-semibold">{selectedOffer.fighter_name}</span>?
            </p>

            <div className="bg-emerald-950/20 border border-emerald-700/40 rounded p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Event:</span>
                  <span className="text-zinc-200">{selectedOffer.fight_card_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Circuit:</span>
                  <span className="text-zinc-200">{selectedOffer.circuit_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Opponent:</span>
                  <span className="text-zinc-200">{selectedOffer.opponent_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Date:</span>
                  <span className="text-zinc-200">
                    {new Date(selectedOffer.event_date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-950/20 border border-blue-700/40 rounded p-3">
              <p className="text-xs text-blue-300">
                ℹ️ Your gladiator will be committed to this fight. Make sure they are ready and in good condition.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setActionModal(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                className={btn() + " flex-1 bg-emerald-600 hover:bg-emerald-500"}
                disabled={busy}
              >
                {busy ? "Accepting..." : "Confirm Accept"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Decline Modal */}
      {actionModal === "decline" && selectedOffer && (
        <Modal open onClose={() => setActionModal(null)} title="Decline Fight Offer">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Decline fight offer for <span className="text-amber-300 font-cinzel font-semibold">{selectedOffer.fighter_name}</span>?
            </p>

            <div className="bg-red-950/20 border border-red-700/40 rounded p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Event:</span>
                  <span className="text-zinc-200">{selectedOffer.fight_card_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Opponent:</span>
                  <span className="text-zinc-200">{selectedOffer.opponent_name}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-950/20 border border-amber-700/40 rounded p-3">
              <p className="text-xs text-amber-300">
                ⚠️ This action cannot be undone. The circuit editor will be notified of your decision.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setActionModal(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleDecline}
                className={btn() + " flex-1 bg-red-600 hover:bg-red-500"}
                disabled={busy}
              >
                {busy ? "Declining..." : "Confirm Decline"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
